import { useTranslation } from 'react-i18next';
import { AlertPrimary } from '@/components/AlertPrimary';
import { CheckExpandable } from '@/components/CheckExpandable';
import { SectionHeader } from '@/components/SectionHeader';
import { Card, Form, Grid, Input, InputNumber, Switch } from '@arco-design/web-react';
import styled from 'styled-components';
import { GrayBox, ItemTitle } from '../tools';
import { usePodSecurityContextStore } from './usePodSecurityContextStore';

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;

export function PodSecurityContext() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    isPodSecurityContextSelected,
    togglePodSecurityContextSelected,
    podSecurityContext,
    updatePodSecurityContext,
  } = usePodSecurityContextStore();

  const msg = {
    pod_security_context_title: t('components:container.security.context'),
    pod_security_context_description: t('components:customize.container.security.settings'),
    tips: t(
      'components:if.both.pod.security.context.and.container.security.context.set.user.group.and.selinux.context.the.container.security.context.settings.will.override.the.pod.security.context.settings',
    ),
    user_and_group: t('components:user.and.group'),
    run_as_non_root: t('components:only.allow.non.root.users.to.run'),
    run_as_non_root_desc: t(
      'components:check.if.the.container.will.run.as.root.before.starting.it.if.the.container.will.run.as.root.do.not.start.it',
    ),
    run_as_user: t('common:user'),
    run_as_user_placeholder: t(
      'components:uid.of.the.container.process.entry.point.defaults.to.uid.specified.in.image.metadata',
    ),
    run_as_group: t('common:user.group'),
    run_as_group_placeholder: t(
      'components:gid.of.the.container.process.entry.point.defaults.to.the.runtime.s.default.gid',
    ),
    selinux_context: t('components:selinux.context'),
    level: t('components:level'),
    role: t('common:role'),
    type: t('common:type'),
    user: t('common:user'),
  };

  const Header = (
    <SectionHeader
      inline
      title={msg.pod_security_context_title}
      description={msg.pod_security_context_description}
    />
  );
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={podSecurityContext}
      onValuesChange={(_, values) => {
        updatePodSecurityContext(values);
      }}
    >
      <FormItem>
        <Card>
          <CheckExpandable
            label={Header}
            checked={isPodSecurityContextSelected}
            onChange={togglePodSecurityContextSelected}
          >
            <Content>
              <AlertPrimary content={msg.tips} style={{ marginTop: '1rem' }} />
              <GrayBox>
                <ItemTitle>{msg.user_and_group}</ItemTitle>
                <Label>
                  <FormItem field="runAsNonRoot" triggerPropName="checked">
                    <Switch />
                  </FormItem>
                  <LabelItem>
                    <span>{msg.run_as_non_root}</span>
                    <p>{msg.run_as_non_root_desc}</p>
                  </LabelItem>
                </Label>
                <FormItem field="runAsUser" label={msg.run_as_user}>
                  <InputNumber placeholder={msg.run_as_user_placeholder} />
                </FormItem>
                <FormItem field="runAsGroup" label={msg.run_as_group}>
                  <InputNumber placeholder={msg.run_as_group_placeholder} />
                </FormItem>
              </GrayBox>
              <GrayBox>
                <ItemTitle>{msg.selinux_context}</ItemTitle>
                <Row gutter={8} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.level" label={msg.level}>
                      <Input />
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.role" label={msg.role}>
                      <Input />
                    </FormItem>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.type" label={msg.type}>
                      <Input />
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.user" label={msg.user}>
                      <Input />
                    </FormItem>
                  </Col>
                </Row>
              </GrayBox>
            </Content>
          </CheckExpandable>
        </Card>
      </FormItem>
    </Form>
  );
}

/** ============================ 样式 ==================================== */
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 8px;
  .arco-form-item {
    margin-bottom: 0;
    width: auto; // 修复 Switch 宽度问题
  }
`;

const LabelItem = styled.div`
  display: flex;
  flex-direction: column;
  color: #000;
  p {
    font-size: 12px;
    opacity: 0.5;
  }
`;
